/**
 * `/automations` — the firm's repeatable workflows (they were Skills in Firm
 * Knowledge). Each one says which choices to make and the steps to follow;
 * run it from a case's chat or from Case Automations. Members can look; who changes things is the firm's choice
 * in Settings > Permissions (everyone by default).
 */
import { Building2, Plus, Zap } from "lucide-react";
import type { FC } from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams
} from "react-router-dom";

import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { PageHeader } from "@/components/ui/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import { useFirmLibrary } from "@/features/firmKnowledge/hooks/useFirmLibrary";
import { useFirmScope } from "@/features/firmKnowledge/hooks/useFirmScope";
import { useFirmSkills } from "@/features/firmKnowledge/hooks/useFirmSkills";

import { SkillsTab } from "../components/SkillsTab";
import { automationsPath, automationsRedirectTarget } from "../routes";

export const AutomationsPage: FC = () => {
  const firm = useFirmScope();
  const library = useFirmLibrary(firm.scope);
  const skills = useFirmSkills(firm.scope);
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  // The builder's split view takes the whole page; its own bar leads back.
  const building =
    params.has("skill") ||
    params.has("from_thread") ||
    params.get("new") === "1";

  if (!firm.lawFirmId) {
    return (
      <EmptyState
        icon={Building2}
        title="No Firm Yet"
        description="Automations are the workflows a firm teaches its agents to run. Join or set up a firm to use them."
        className="h-full"
        data-testid="automations-empty"
      />
    );
  }

  // A stale `?tab=skills` link: drop the tab, keep the rest.
  const stale = automationsRedirectTarget(location.search);
  if (stale) return <Navigate to={stale} replace />;

  const whose =
    firm.isSuperAdmin && firm.scope.lawFirmId ? "this firm" : "your firm";

  return (
    <div
      className="flex h-full min-h-0 w-full flex-col bg-surface"
      data-testid="automations-page"
    >
      {building ? null : (
        <div className="flex shrink-0 px-6 pt-6 pb-3">
          <PageHeader
            title={
              <span className="inline-flex items-center gap-2">
                <Zap className="size-4 text-accent-seed" aria-hidden />
                Automations
              </span>
            }
            description={`The repeatable workflows ${whose} runs from a case, a client or the practice, once or on a schedule.`}
            actions={
              !firm.permissionsReady ? (
                <Skeleton
                  className="h-7 w-32 rounded-md"
                  data-testid="new-skill-loading"
                />
              ) : firm.canEdit ? (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate(automationsPath({ newSkill: true }))}
                  data-testid="new-skill"
                >
                  <Plus />
                  New Automation
                </Button>
              ) : null
            }
            className="w-full"
          />
        </div>
      )}
      <div
        className="min-h-0 flex-1 overflow-hidden"
        data-building={building || undefined}
      >
        <SkillsTab skills={skills} library={library} firm={firm} />
      </div>
    </div>
  );
};
